/**
 * Relevance for the "Relevance: X%" pill on search results.
 *
 * An AI/deep search scores every result itself (meta.scores_are_relevance);
 * that score is used as is. A quick search (Typesense or the SQL fallback)
 * shows a keyword match: for each meaningful word of the query, where it
 * appears on the card (title 1.0, keywords 0.85, summary 0.75, industry 0.5,
 * nowhere 0), averaged. Words match without their plural ending and on a
 * shared stem (the first six letters of longer words), so "biopsy" finds
 * "biopsies" and "accessibility" finds "accessible".
 *
 * The server now works that score out itself (search_v2/keyword_score.py,
 * the same rule, kept in step by hand) over the WHOLE result set, sorts by it
 * and sends it as the percentage with meta.score_kind "keyword" — so page 1
 * holds the highest scores and the pill is the number the order was built
 * from. The browser only computes it when the server did not (the switch is
 * off, or a deep search ran without its AI scores).
 */

export type RelevanceKind = 'ai' | 'keyword';

const STOP = new Set([
  'a', 'an', 'and', 'the', 'of', 'for', 'in', 'on', 'to', 'with', 'by', 'or', 'from', 'at', 'as', 'is', 'are',
  'be', 'using', 'based', 'via', 'into', 'new', 'novel', 'method', 'methods', 'system', 'systems', 'technology',
  'technologies', 'tech',
]);

const words = (s: unknown): string[] =>
  String(s ?? '').toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '')
    .split(/[^a-z0-9]+/).filter(Boolean);

/** Arrays, JSON strings and objects (industry categories) → one text. */
const flat = (v: unknown): string => {
  if (v == null) return '';
  if (typeof v === 'string') {
    const t = v.trim();
    if ((t.startsWith('[') || t.startsWith('{'))) {
      try { return flat(JSON.parse(t)); } catch { return t; }
    }
    return t;
  }
  if (Array.isArray(v)) return v.map(flat).join(' ');
  if (typeof v === 'object') return Object.values(v as Record<string, unknown>).map(flat).join(' ');
  return String(v);
};

/** The word and its possible singulars ("biopsies" → biopsy, "viruses" →
 *  virus, "lungs" → lung). Two words match when these sets share a form, so
 *  a plural is never a miss (the search index stems the same way) and "cause"
 *  still meets "causes". Stray forms like "viru" only match another stray. */
const forms = (w: string): string[] => {
  const out = [w];
  if (w.length > 3 && w.endsWith('s') && !w.endsWith('ss')) out.push(w.slice(0, -1));
  if (w.length > 4 && w.endsWith('es')) out.push(w.slice(0, -2));
  if (w.length > 4 && w.endsWith('ies')) out.push(w.slice(0, -3) + 'y');
  return out;
};

const stem = (w: string): string => (w.length >= 7 ? w.slice(0, 6) : w);

const hits = (list: string[], t: string): boolean => {
  const tf = forms(t);
  // Shared stem (first six letters of a longer word): "accessibility" finds
  // "accessible". Only for query words of five letters or more.
  const stems = tf.filter(f => f.length >= 5).map(stem);
  return list.some(w => {
    const wf = forms(w);
    return wf.some(x => tf.includes(x) || stems.some(s => x.startsWith(s)));
  });
};

/** 0–100, or null when the query has no meaningful word (nothing to match). */
export function keywordRelevance(query: string, m: Record<string, any> | null | undefined): number | null {
  const q = Array.from(new Set(words(query).filter(w => w.length > 1 && !STOP.has(w))));
  if (!q.length || !m) return null;
  // Both titles, as the search index and the server's score read both.
  const title = words((m.utech_title || '') + ' ' + (m.title || ''));
  const kw = words(flat(m.utech_keywords) + ' ' + flat(m.utech_keywords_list) + ' ' + flat(m.keywords));
  const summary = words((m.utech_summary || '') + ' ' + (m.short_summary || '') + ' ' + (m.summary || ''));
  const cats = words(flat(m.utech_industry_categories) + ' ' + flat(m.industry_categories)
    + ' ' + flat(m.major_categories) + ' ' + flat(m.subcategories));
  let total = 0;
  for (const t of q) {
    total += hits(title, t) ? 1 : hits(kw, t) ? 0.85 : hits(summary, t) ? 0.75 : hits(cats, t) ? 0.5 : 0;
  }
  return Math.round((100 * total) / q.length);
}

/** The pill's value for one search result, and what kind of score it is. */
export function resultRelevance(
  r: { percentage?: unknown; metadata?: Record<string, any> } | null | undefined,
  query: string,
  scoresAreRelevance: boolean,
  scoreKind?: string | null,
): { value: number | null; kind: RelevanceKind | null } {
  // A query that was only a filter ("IIT Bombay"): nothing to score, so no
  // pill — scoring the words in the browser read 0% on most cards.
  if (scoreKind === 'filter') return { value: null, kind: null };
  if (!r) return { value: null, kind: null };
  const pct = typeof r.percentage === 'number' && isFinite(r.percentage) ? r.percentage : null;
  if (scoresAreRelevance && pct !== null) {
    return { value: pct, kind: 'ai' };
  }
  // The server's keyword score: the one the results were sorted by.
  if (scoreKind === 'keyword' && pct !== null) {
    return { value: Math.round(pct), kind: 'keyword' };
  }
  const v = keywordRelevance(query, r.metadata);
  return v === null ? { value: null, kind: null } : { value: v, kind: 'keyword' };
}
